import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ChevronLeft } from "lucide-react";
import { workOrdersApi } from "../api/queries/work_orders";
import { WorkOrderTimeline } from "../components/WorkOrderTimeline";
import { StatusDot, workOrderTone } from "../components/StatusDot";
import { formatRelative } from "../lib/time";
import { toast } from "../components/Toast";

const TERMINAL = new Set(["complete", "failed", "cancelled"]);

export function WorkOrderDetailPage() {
  // See DeviceDetail.tsx for the rationale — route is auto-namespaced
  // under "_protected", `from` doesn't match, so use `strict: false`.
  const { id } = useParams({ strict: false }) as { id: string };
  const nav = useNavigate();
  const qc = useQueryClient();

  const order = useQuery({
    queryKey: ["work-order", id],
    queryFn: () => workOrdersApi.get(id),
  });

  const cancel = useMutation({
    mutationFn: () => workOrdersApi.cancel(id),
    onSuccess: () => {
      toast("Work order cancelled");
      qc.invalidateQueries({ queryKey: ["work-order", id] });
    },
    onError: (e: Error) => toast(e.message ?? "Failed to cancel", "error"),
  });

  if (order.isLoading)
    return (
      <main className="grid h-[calc(100vh-var(--topnav-h))] place-items-center">
        <span className="spinner" aria-hidden="true" />
      </main>
    );
  if (!order.data)
    return (
      <main className="grid h-[calc(100vh-var(--topnav-h))] place-items-center text-secondary">
        Work order not found
      </main>
    );

  const wo = order.data;
  const tone = workOrderTone(wo.status);
  const isTerminal = TERMINAL.has(wo.status);

  return (
    <main className="mx-auto max-w-[1100px] px-6 py-6">
      <button
        type="button"
        onClick={() => nav({ to: "/work-orders" })}
        className="btn btn--ghost btn--sm mb-3"
      >
        <ChevronLeft size={14} /> All orders
      </button>

      <header className="card flex flex-col gap-3 p-5">
        <div className="flex items-start gap-3">
          <StatusDot tone={tone} pulse={tone === "running"} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-baseline gap-2">
              <h1 className="text-[20px] font-semibold tracking-[-0.01em]">{wo.title}</h1>
              <span className="font-mono text-[11px] text-tertiary">{wo.status}</span>
            </div>
            {wo.description && (
              <p className="mt-1 text-[13px] text-secondary leading-[20px]">{wo.description}</p>
            )}
            <div className="mt-2 text-[12px] text-tertiary">
              {wo.requested_by_email ? `Requested by ${wo.requested_by_email} · ` : ""}
              {formatRelative(wo.created_at)}
            </div>
          </div>
          {!isTerminal && (
            <button
              className="btn btn--danger btn--sm shrink-0"
              onClick={() => cancel.mutate()}
              disabled={cancel.isPending}
              type="button"
            >
              Cancel order
            </button>
          )}
        </div>
      </header>

      {wo.ceo_excerpt.length > 0 && (
        <section className="mt-4">
          <h2 className="micro mb-2">CEO conversation</h2>
          <div className="card divide-y divide-border-subtle">
            {wo.ceo_excerpt.map((m, i) => (
              <div key={i} className="px-4 py-3">
                <div className="micro mb-1">{m.role === "user" ? "You" : "CEO"}</div>
                <div className="text-[13px] leading-[20px] text-primary whitespace-pre-wrap">
                  {m.content}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mt-4">
        <h2 className="micro mb-2">Steps</h2>
        <WorkOrderTimeline steps={wo.steps} />
      </section>
    </main>
  );
}
